import { collection, Timestamp, } from "firebase/firestore";
import { warnUnknownFields } from "./converterWarnings";
const OUTBOUND_PLAYBOOK_RUN_FIELDS = [
    "id",
    "path",
    "companyId",
    "playbookId",
    "playbookVersion",
    "status",
    "origin",
    "playbookSnapshot",
    "taskCounts",
    "createdAt",
    "updatedAt",
    "startedAt",
    "completedAt",
    "cancelledAt",
    "lastTaskStateChangedAt",
    "error",
];
const OUTBOUND_TASK_FIELDS = [
    "id",
    "path",
    "companyId",
    "playbookRunId",
    "playbookId",
    "playbookVersion",
    "agentId",
    "recipientPhoneNumber",
    "status",
    "originType",
    "input",
    "result",
    "latestAttemptId",
    "latestAttemptStatus",
    "latestOutcome",
    "attemptCount",
    "maxAttempts",
    "nextScheduledAt",
    "schedulingReason",
    "workerType",
    "claimedByWorkerId",
    "leaseExpiresAt",
    "createdAt",
    "updatedAt",
    "startedAt",
    "completedAt",
    "failedAt",
    "cancelledAt",
    "error",
    "errorCode",
    "recoveryReview",
];
const OUTBOUND_TASK_ATTEMPT_FIELDS = [
    "id",
    "path",
    "companyId",
    "taskId",
    "playbookRunId",
    "playbookId",
    "playbookVersion",
    "workerId",
    "status",
    "attemptNumber",
    "provider",
    "providerOpaqueId",
    "providerCreateRequestedAt",
    "providerCallContext",
    "providerCallSid",
    "providerRoomName",
    "providerRoomSid",
    "outcome",
    "summary",
    "structuredResult",
    "transcriptRef",
    "recordingRef",
    "error",
    "errorCode",
    "createdAt",
    "updatedAt",
    "startedAt",
    "completedAt",
    "failedAt",
    "heartbeatAt",
];
function toDate(value) {
    if (value instanceof Date)
        return value;
    if (value instanceof Timestamp)
        return value.toDate();
    if (typeof value === "string") {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
}
function payload(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function numeric(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}
const outboundPlaybookRunConverter = {
    toFirestore(value) {
        return value;
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        warnUnknownFields("packages/ui OutboundPlaybookRun.fromFirestore", data, OUTBOUND_PLAYBOOK_RUN_FIELDS);
        const taskCounts = payload(data.taskCounts);
        return {
            id: snapshot.id,
            path: snapshot.ref.path,
            companyId: snapshot.ref.parent.parent?.id || data.companyId || "",
            playbookId: data.playbookId || "",
            playbookVersion: numeric(data.playbookVersion, 1),
            status: (data.status || "pending"),
            origin: payload(data.origin),
            playbookSnapshot: payload(data.playbookSnapshot),
            taskCounts: {
                total: numeric(taskCounts.total),
                pending: numeric(taskCounts.pending),
                scheduled: numeric(taskCounts.scheduled),
                inProgress: numeric(taskCounts.inProgress),
                paused: numeric(taskCounts.paused),
                completed: numeric(taskCounts.completed),
                failed: numeric(taskCounts.failed),
                cancelled: numeric(taskCounts.cancelled),
            },
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            startedAt: toDate(data.startedAt),
            completedAt: toDate(data.completedAt),
            cancelledAt: toDate(data.cancelledAt),
            lastTaskStateChangedAt: toDate(data.lastTaskStateChangedAt),
            error: typeof data.error === "string" ? data.error : null,
        };
    },
};
export const outboundTaskConverter = {
    toFirestore(value) {
        return {
            ...value,
            agentId: value.agentId,
            recipientPhoneNumber: value.recipientPhoneNumber,
            ...(value.recoveryReview !== undefined
                ? { recoveryReview: value.recoveryReview }
                : {}),
        };
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        warnUnknownFields("packages/ui OutboundTask.fromFirestore", data, OUTBOUND_TASK_FIELDS);
        return {
            id: snapshot.id,
            path: snapshot.ref.path,
            companyId: snapshot.ref.parent.parent?.id || data.companyId || "",
            playbookRunId: data.playbookRunId || "",
            playbookId: data.playbookId || "",
            playbookVersion: numeric(data.playbookVersion, 1),
            agentId: typeof data.agentId === "string" ? data.agentId : null,
            recipientPhoneNumber: typeof data.recipientPhoneNumber === "string"
                ? data.recipientPhoneNumber
                : null,
            status: (data.status || "pending"),
            originType: (data.originType || "api"),
            input: payload(data.input),
            result: data.result == null ? null : payload(data.result),
            latestAttemptId: typeof data.latestAttemptId === "string" ? data.latestAttemptId : null,
            latestAttemptStatus: typeof data.latestAttemptStatus === "string"
                ? data.latestAttemptStatus
                : null,
            latestOutcome: typeof data.latestOutcome === "string"
                ? data.latestOutcome
                : null,
            attemptCount: numeric(data.attemptCount),
            maxAttempts: numeric(data.maxAttempts, 1),
            nextScheduledAt: toDate(data.nextScheduledAt),
            schedulingReason: typeof data.schedulingReason === "string"
                ? data.schedulingReason
                : null,
            workerType: "outbound_call",
            claimedByWorkerId: typeof data.claimedByWorkerId === "string"
                ? data.claimedByWorkerId
                : null,
            leaseExpiresAt: toDate(data.leaseExpiresAt),
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            startedAt: toDate(data.startedAt),
            completedAt: toDate(data.completedAt),
            failedAt: toDate(data.failedAt),
            cancelledAt: toDate(data.cancelledAt),
            error: typeof data.error === "string" ? data.error : null,
            errorCode: typeof data.errorCode === "string" ? data.errorCode : null,
            recoveryReview: data.recoveryReview && typeof data.recoveryReview.reason === "string"
                ? {
                    reason: data.recoveryReview.reason,
                    detectedAt: toDate(data.recoveryReview.detectedAt),
                }
                : null,
        };
    },
};
export const outboundTaskAttemptConverter = {
    toFirestore(value) {
        return value;
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        warnUnknownFields("packages/ui OutboundTaskAttempt.fromFirestore", data, OUTBOUND_TASK_ATTEMPT_FIELDS);
        return {
            id: snapshot.id,
            path: snapshot.ref.path,
            companyId: snapshot.ref.parent.parent?.id || data.companyId || "",
            taskId: data.taskId || "",
            playbookRunId: data.playbookRunId || "",
            playbookId: data.playbookId || "",
            playbookVersion: numeric(data.playbookVersion, 1),
            workerId: data.workerId || "",
            status: (data.status || "claimed"),
            attemptNumber: numeric(data.attemptNumber, 1),
            provider: "twilio_livekit",
            providerOpaqueId: typeof data.providerOpaqueId === "string"
                ? data.providerOpaqueId
                : null,
            providerCreateRequestedAt: toDate(data.providerCreateRequestedAt),
            providerCallContext: data.providerCallContext &&
                typeof data.providerCallContext === "object"
                ? {
                    ...data.providerCallContext,
                    agentCallAuth: {
                        ...data.providerCallContext.agentCallAuth,
                        issuedAt: toDate(data.providerCallContext.agentCallAuth?.issuedAt),
                        expiresAt: toDate(data.providerCallContext.agentCallAuth?.expiresAt),
                        revokedAt: toDate(data.providerCallContext.agentCallAuth?.revokedAt),
                    },
                }
                : null,
            providerCallSid: typeof data.providerCallSid === "string"
                ? data.providerCallSid
                : null,
            providerRoomName: typeof data.providerRoomName === "string"
                ? data.providerRoomName
                : null,
            providerRoomSid: typeof data.providerRoomSid === "string"
                ? data.providerRoomSid
                : null,
            outcome: typeof data.outcome === "string"
                ? data.outcome
                : null,
            summary: typeof data.summary === "string" ? data.summary : null,
            structuredResult: data.structuredResult == null ? null : payload(data.structuredResult),
            transcriptRef: typeof data.transcriptRef === "string" ? data.transcriptRef : null,
            recordingRef: typeof data.recordingRef === "string" ? data.recordingRef : null,
            error: typeof data.error === "string" ? data.error : null,
            errorCode: typeof data.errorCode === "string" ? data.errorCode : null,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            startedAt: toDate(data.startedAt),
            completedAt: toDate(data.completedAt),
            failedAt: toDate(data.failedAt),
            heartbeatAt: toDate(data.heartbeatAt),
        };
    },
};
export function outboundPlaybookRunsCollection(db, companyId) {
    return collection(db, "companies", companyId, "outboundPlaybookRuns").withConverter(outboundPlaybookRunConverter);
}
export function outboundTasksCollection(db, companyId) {
    return collection(db, "companies", companyId, "outboundTasks").withConverter(outboundTaskConverter);
}
export function outboundTaskAttemptsCollection(db, companyId) {
    return collection(db, "companies", companyId, "outboundTaskAttempts").withConverter(outboundTaskAttemptConverter);
}
